import {
  type CSSProperties,
  type DependencyList,
  forwardRef,
  Fragment,
  isValidElement,
  type ReactElement,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useDebounceFn,
  useDeepCompareEffect,
  useMemoizedFn,
  useSafeState,
  useUpdate,
  useUpdateEffect,
} from 'ahooks';
import { Button, message, Spin, Tree, type TreeProps } from 'antd';
import { CaretDownFilled } from '@ant-design/icons';
import { isArray, isString } from '@dimjs/lang';
import { cloneState } from '@dimjs/model';
import { arrayRemove, classNames, extend, get } from '@dimjs/utils';
import {
  findParentsElement,
  getUuid,
  isUndefinedOrNull,
  type TAny,
  toArray,
  type TPlainObject,
  treeFilter,
} from '@flatbiz/utils';
import {
  ButtonOperate,
  type ButtonOperateProps,
} from '../button-operate/index.js';
import { fbaHooks } from '../fba-hooks/index.js';
import { InputSearchWrapper } from '../input-search-wrapper/index.js';
import type { TRequestStatusProps } from '../request-status/index.js';
import { RequestStatus } from '../request-status/index.js';
import { treeWrapperModel } from './model.js';
import {
  completionParentChildrenNode,
  getActualSelectKeyList,
  getDefaultExpandAllKeys,
  getExpandedKeys,
  getVauleList,
  onTreeDrop,
  removeAllParentNode,
  removeAllSelectLeafNode,
  removeTargetNodeAndAllChildrenNode,
} from './utils.js';
import './style.less';

type TreeWrapperValue =
  | string
  | number
  | Array<string | number>
  | TPlainObject<string | number>
  | Array<TPlainObject<string | number>>;

export type TreeServiceConfig = {
  params?: TPlainObject;
  requiredParamsKeys?: string[];
  onRequest?: (params?: TAny) => TAny;
  /**
   * 响应数据适配器
   */
  onRequestResultAdapter?: (respData: TAny) => TPlainObject[];
};
export type TreeLoadDataServiceConfig = {
  getParams?: (dataItem: TPlainObject) => TPlainObject;
  onRequest: (params: TPlainObject) => TAny;
  /**
   * 响应数据适配器
   */
  onRequestResultAdapter?: (respData: TAny) => TPlainObject[];
};

export type TreeWrapperMenuItem = {
  title: string;
  onClick: (dataItem: TPlainObject, event) => void;
  icon?: ReactElement;
};

export type TreeWrapperProps = Omit<
  TreeProps,
  | 'treeData'
  | 'onExpand'
  | 'selectedKeys'
  | 'checkedKeys'
  | 'onCheck'
  | 'onSelect'
  | 'fieldNames'
  | 'multiple'
  | 'loadData'
  | 'icon'
  | 'defaultCheckedKeys'
  | 'defaultExpandParent'
  | 'defaultSelectedKeys'
> & {
  /**
   * 设置请求唯一值
   * ```
   * 1. 设置后，如果请求入参不变，不会重复请求接口，直接使用上次缓存数据
   * ```
   */
  modelKey?: string;
  /**
   * 字段映射
   * ```
   * 默认值：
   * label = 'label'
   * value = 'value'
   * children = 'children'
   *
   * 如果未设置list，则说明接口返回数据为数组
   * 如果返回数据存在多级，可通过设置 list='a.b.list'
   * ```
   */
  fieldNames?: {
    label?: string;
    value?: string;
    children?: string;
    list?: string;
  };
  /**
   * useEffect依赖项数组，用于重新发起获取接口数据
   */
  effectDependencyList?: DependencyList;
  /**
   * 请求服务需求的数据，当设置`selectorTreeList`后无效果
   */
  serviceConfig?: TreeServiceConfig;
  /**
   * 是否开启异步加载
   */
  loadDataFlag?: boolean;
  /**
   * 异步加载数据配置
   * ```
   * 1. 会通过fieldNames配置label、value、children进行转义
   * 2. fieldNames.list 配置在此处无效
   * ```
   */
  loadDataServiceConfig?: TreeLoadDataServiceConfig;
  /**
   * 当设置selectorTreeList后，serviceConfig将失效
   * ```
   * 1. 不支持异步数据，异步使用serviceConfig方式
   * ```
   */
  selectorTreeList?: TPlainObject[];
  /**
   * 通过服务获取数据后回调，当设置`selectorList`后无效果
   * ```
   * 相同modelKey，同时发起多个渲染时，只有第一个会执行 onSelectorTreeListChange 回调
   * ```
   */
  onSelectorTreeListChange?: (dataList: TPlainObject[]) => void;
  /** 接口响应数据变更 */
  onRequestResponseChange?: (data: TAny) => void;
  onChange?: (
    selectedKey?: TreeWrapperValue,
    operateNodeData?: TPlainObject[] | TPlainObject,
    operateAllNodeDataList?: TPlainObject[],
    extraData?: TPlainObject
  ) => void;

  /**
   * 搜索关键字，打开tree折叠过滤关键字
   */
  searchValue?: string;
  /**
   * checkable模式下，onChange是否返回父节点，默认值true
   * @description 已过期，请使用 checkableType 配置
   * ```
   * 注意：checkStrictly = true，checkableResponseParentNode配置失效
   * ```
   */
  checkableResponseParentNode?: boolean;
  /**
   * 节点选择类型（默认值：'2'）, 多选时有效
   * ```
   * '1' 表示返回所有选中的节点（包含父节点和叶子节点）
   * '2' 表示只返回叶子节点
   * '3' 表示当叶子节点全选时，返回父节点，不返回下面的叶子节点
   *
   *  注意：checkStrictly = true，checkableType配置失效
   * ```
   */
  checkableType?: '1' | '2' | '3';
  /**
   * 菜单触发类型，默认：click
   * ```
   * 自定义设置 titleRender 后失效
   * ```
   */
  menuTriggerType?: 'click' | 'hover';
  menus?: (dataItem) => ButtonOperateProps;

  showSearch?: boolean;
  onSearchValueChange?: (searchValue?: string) => void;
  searchPlaceholder?: string;
  searchStyle?: CSSProperties;
  icon?: (data: {
    isParent: boolean;
    isLeaf: boolean;
    [key: string]: TAny;
  }) => ReactElement;
  /**
   * 状态文案配置
   */
  requestMessageConfig?: TRequestStatusProps['messageConfig'];
  /**
   * value格式
   *```
   * 1. string 、number
   * 2. Array<string | number>
   * 3. lableInValue = true，根据labelInValueFieldNames配置格式
   * 4. lableInValue = true，Array<labelInValueFieldNames配置>
   * ```
   */
  value?: TreeWrapperValue;
  /**
   * 选择的节点数据，是否包含label信息
   * ```
   * 默认选择的节点只有value数据
   * ```
   */
  labelInValue?: boolean;
  /**
   * ```
   * @deprecated 废弃
   * lableInValue = true，onChange输出数据字段名称，默认：{ label: string; value: string|number }
   * ```
   */
  labelInValueFieldNames?: { label: string; value: string };
  /**
   * 禁用状态是否可选，默认值true
   * 当dateItem中包含 disabled 则数据为禁用状态
   */
  disabledCanUse?: boolean;

  /** treeItem数据适配器 */
  treeItemDataAdapter?: (dataItem: TPlainObject) => TPlainObject;
  /** 搜索位置额外元素 */
  searchExtraElement?: ReactElement;
  /** 有唯一跟节点时，初始化是否展开，默认值：false */
  initRootExpand?: boolean;
  /** 搜素过滤方式，高亮 | 过滤；默认：filter */
  searchResultType?: 'highlight' | 'filter';
  /**
   * 拖拽节点处理，自定义onDrop事件后，失效
   * ```
   * 参数
   * 1. parentId 拖拽结束位置父节点ID
   * 2. id 当前拖拽节点ID
   * 3. index 拖拽结束位置所在数组下标
   * ```
   */
  onDropNodeHandle?: (result: {
    parentId?: string | number;
    id: string | number;
    index: number;
  }) => void;
  /**
   * 拖拽排序前判断，如果返回 true，可排序，其他不可排序
   * ```
   * 可用于需要权限控制判断
   * ```
   */
  onDropPrev?: (info) => boolean;
  /**
   * 标签渲染
   * ```
   * 1. 自定义设置 titleRender 后失效
   * 2. 与titleRender的区别
   *    a. 设置 labelRender 后，menuLayout有效
   *    b. 设置 titleRender 后，menuLayout失效
   * ```
   */
  labelRender?: (nodeData: TPlainObject) => ReactElement;
  /**
   * 自定义搜索规则
   */
  customSearchRule?: (nodeData: TPlainObject, searchValue?: string) => boolean;

  /**
   * 点击父节点 是否切换折叠
   * ```
   * 1. 设置 titleRender 后，此配置失效
   * ```
   */
  clickParentNodeToggleExpand?: boolean;
  /**
   * 禁用节点选中
   * ```
   * 1. checkable = true，此配置失效
   * 2. 在未设置节点 disabled 属性情况下，满足此配置不会触发onChange事件
   * ```
   */
  disableNodeSelect?: (item: TPlainObject) => boolean;
  /**
   * 是否必选，最后一个不能取消
   * ```
   * 1. 设置 checkable = true 时无效
   * ```
   */
  required?: boolean;

  /**
   * 在第一次渲染value时，主动执行一次onChange
   * ```
   * 1. 设置labelInValue = true 时有效
   * 2. 不考虑子父节点的关系
   * ```
   */
  executeOnChangeInRenderFirstValue?: boolean;
};

const compareArrays = (arr1, arr2) => {
  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();
  return JSON.stringify(sortedArr1) === JSON.stringify(sortedArr2);
};

export type TreeWrapperRefApi = {
  onClearSelectorList: () => void;
  getTreeDataList: () => TreeProps['treeData'];
  onChangeExpandedKeys: (expandedKeys: Array<string | number>) => void;
  getTreeTiledArray: () => TPlainObject[];
  getTreeTiledArrayMap: () => TPlainObject;
  /** 选择所有 */
  onSelectAll: () => void;
  /** 取消选择所有 */
  onCancelSelectAll: () => void;
  /** 展开所有 */
  onExpandedAll: () => void;
  /** 取消展开所有 */
  onCancelExpandedAll: () => void;
  onRefresh: () => void;
};

/**
 * Tree包装组件，默认返回父节点，可配置不返回
 * ```
 * 1. treeNode内置字段说明（如需要相关功能，可在接口中添加固定字段）
 *    a. disabled 禁掉响应
 *    b. isLeaf  设置为叶子节点 (设置了 loadData 时有效)。为 false 时会强制将其作为父节点
 *    c. disableCheckbox  checkable模式下，treeNode复选框是否可选
 * 2. 当设置selectorTreeList属性后，serviceConfig将失效
 * 3. checkable=true，为多选模式
 * 4. 设置value后，组件显示受控
 * 5. 设置loadDataFlag=true，会动态获取children，当treeNode中包含isLeaf=true字段，表示为叶子节点，没有children了
 * 6. 内置 onDrop 事件已处理数组排序，通过 onDropNodeHandle 事件可获取操作节点排序数据；自定义onDrop后，内置onDrop失效
 * ```
 */
export const TreeWrapper = forwardRef<TreeWrapperRefApi, TreeWrapperProps>(
  (props, ref) => {
    const {
      style,
      serviceConfig,
      effectDependencyList,
      selectorTreeList,
      value,
      onChange,
      modelKey,
      checkableResponseParentNode = true,
      checkable,
      onSelectorTreeListChange,
      onRequestResponseChange,
      treeItemDataAdapter,
      searchValue,
      showSearch,
      searchPlaceholder,
      requestMessageConfig,
      labelInValue,
      disabledCanUse,
      menuTriggerType,
      initRootExpand,
      searchResultType,
      onDropNodeHandle,
      onDropPrev,
      labelRender,
      searchStyle,
      className,
      defaultExpandAll,
      menus,
      customSearchRule,
      checkableType,
      clickParentNodeToggleExpand,
      disableNodeSelect,
      required,
      executeOnChangeInRenderFirstValue,
      ...otherProps
    } = props;

    const forceUpdate = useUpdate();

    const modelKeyInner = useMemo(() => {
      return modelKey || getUuid();
    }, [modelKey]);

    // eslint-disable-next-line no-prototype-builtins
    const hasSelectorTreeList = props.hasOwnProperty('selectorTreeList');
    const newServiceConfig = serviceConfig || {};
    const newEffectDependencyList = effectDependencyList || [];
    const [treeExpandedKeys, setTreeExpandedKeys] = useState<
      Array<string | number>
    >([]);
    const [state, actions] = treeWrapperModel(modelKeyInner).useStore();
    const [loading, setLoading] = useSafeState(false);
    const requestPreKey = `request-progress-${modelKeyInner}`;
    const searchTypeHighlight = searchResultType === 'highlight';
    const fieldNames = useMemo(() => {
      return {
        label: 'label',
        value: 'value',
        children: 'children',
        ...props.fieldNames,
      };
    }, [props.fieldNames]);
    const menuTriggerTypeNew =
      menuTriggerType === undefined ? 'click' : menuTriggerType;
    const responseFirstRef = useRef(true);
    const innerValueListRef = useRef<any[] | undefined>(undefined);

    const labelInValueFieldNamesMerge = useMemo(() => {
      return { label: 'label', value: 'value' };
    }, []);
    // tree 搜索值
    const [treeSearchValue, setTreeSearchValue] = useState<
      string | undefined
    >();

    const valueList = useMemo(() => {
      return getVauleList(value, labelInValueFieldNamesMerge);
    }, [labelInValueFieldNamesMerge, value]);

    fbaHooks.useEffectCustom(() => {
      if (state.treeList.length > 0) {
        if (
          responseFirstRef.current &&
          labelInValue &&
          executeOnChangeInRenderFirstValue &&
          valueList.length > 0
        ) {
          const dataList: TPlainObject[] = [];
          const originalList: TPlainObject[] = [];
          valueList.forEach((item) => {
            const target = state.treeTiledArrayMap[item];
            if (target) {
              originalList.push(target);
              dataList.push({
                [labelInValueFieldNamesMerge.label]: target[fieldNames.label],
                [labelInValueFieldNamesMerge.value]: target[fieldNames.value],
              });
            }
          });
          if (checkable) {
            props.onChange?.(dataList ? dataList : undefined, originalList);
          } else {
            props.onChange?.(
              dataList ? dataList[0] : undefined,
              originalList[0]
            );
          }
        }
        if (responseFirstRef.current && valueList.length === 0) {
          responseFirstRef.current = false;
          if (defaultExpandAll) {
            setTreeExpandedKeys(
              getDefaultExpandAllKeys(state.treeList, fieldNames)
            );
            return;
          } else if (initRootExpand && state.treeList.length) {
            setTreeExpandedKeys([state.treeList[0][fieldNames.value]]);
            return;
          }
        }
        if (valueList.length > 0) {
          responseFirstRef.current = false;
          if (innerValueListRef.current) {
            if (!compareArrays(valueList, innerValueListRef.current)) {
              const innerExpandedKeys = getTreeExpandedKeys(valueList, true);
              setTreeExpandedKeys(
                Array.from(new Set(treeExpandedKeys?.concat(innerExpandedKeys)))
              );
            }
          } else {
            const innerExpandedKeys = getTreeExpandedKeys(valueList, true);
            setTreeExpandedKeys(
              Array.from(new Set(treeExpandedKeys?.concat(innerExpandedKeys)))
            );
          }
        }
      }
      // 当 valueList 为空时，不去清除treeExpandedKeys数据
    }, [state.treeList, JSON.stringify(valueList)]);

    const valueIsEmpty = (data: string | number) => {
      return data === '' || isUndefinedOrNull(data);
    };

    const serviceResponseHandle = (respData) => {
      let result: TPlainObject[] = respData;
      if (newServiceConfig.onRequestResultAdapter) {
        result = newServiceConfig.onRequestResultAdapter(
          respData as unknown as TPlainObject
        );
      } else if (fieldNames.list) {
        result = get(respData, fieldNames.list, []) || [];
      }
      if (!isArray(result)) {
        console.warn(
          '接口返回数据为非数组结构，确认是否需要配置fieldNames.list进行解析'
        );
        return [];
      }
      return result;
    };

    fbaHooks.useEffectCustom(() => {
      setTreeSearchValue(searchValue);
    }, [searchValue]);

    const startDataSourceRequest = useMemoizedFn(async () => {
      try {
        if (!newServiceConfig.onRequest) {
          throw new Error('onRequest 调用接口服务不能为空');
        }
        const requiredParamsKeys = newServiceConfig.requiredParamsKeys;
        const params = extend({}, newServiceConfig.params);
        if (requiredParamsKeys) {
          const isEmpty = requiredParamsKeys.find((key) => {
            return valueIsEmpty(params[key] as string | number);
          });
          if (isEmpty) {
            void actions.changeRequestStatus({
              status: 'no-dependencies-params',
            });
            console.warn(
              `TreeWrapper组件：参数：${requiredParamsKeys.join('、')}不能为空`
            );
            return;
          }
        }
        try {
          setLoading(true);
          await actions.changeRequestStatus({
            status: 'request-progress',
          });
          window[requestPreKey] = true;
          const _respData = await newServiceConfig.onRequest?.(params);
          onRequestResponseChange?.(_respData);
          const respDataListNew = serviceResponseHandle(_respData);
          if (respDataListNew.length === 0) {
            responseFirstRef.current = false;
          }
          onChangeSelectorList(respDataListNew);
          setLoading(false);

          window[requestPreKey] = false;
        } catch (error: TAny) {
          window[requestPreKey] = false;
          setLoading(false);
          void actions.changeRequestStatus({
            status: 'request-error',
            errorMessage: error.message,
          });
        }
      } catch (error: TAny) {
        responseFirstRef.current = false;
        setLoading(false);
        void message.error((error.message as string) || '数据查询异常...');
      }
    });

    useDeepCompareEffect(() => {
      if (hasSelectorTreeList) return;
      // 当无依赖项时，如果存在缓存数据，就不在调用接口
      if (newEffectDependencyList.length) {
        void startDataSourceRequest();
        return;
      }
      const allState = treeWrapperModel(modelKeyInner).getState();
      if (allState.requestStatus === 'request-success') {
        onSelectorTreeListChange?.(allState.treeList);
        return;
      }
      // 判断相同的modelKey是否已经在请求数据中，避免重复请求
      if (!window[requestPreKey]) {
        void startDataSourceRequest();
      } else {
        onSelectorTreeListChange?.(allState.treeList);
      }
    }, newEffectDependencyList);

    fbaHooks.useEffectCustom(() => {
      if (hasSelectorTreeList) {
        onChangeSelectorList(selectorTreeList || []);
      }
    }, [selectorTreeList]);

    const onChangeSelectorList = useMemoizedFn((dataList: TAny[]) => {
      if (dataList?.length === 0 && state.treeList.length === 0) {
        void actions.changeRequestStatus({ status: 'request-success' });
        return;
      }
      void actions.setTreeList({
        treeList: dataList || [],
        childrenName: fieldNames.children,
        valueName: fieldNames.value,
      });
      onSelectorTreeListChange?.(dataList);
    });

    const getTreeExpandedKeys = (
      valueList: Array<string | number>,
      refresh?: boolean
    ) => {
      let newTreeExpandedKeys = [] as Array<string | number>;
      valueList.forEach((value) => {
        if (!refresh && treeExpandedKeys?.includes(value)) return;
        const targetKeys = getExpandedKeys(value, state.treeList, fieldNames);
        newTreeExpandedKeys = newTreeExpandedKeys.concat(targetKeys);
      });
      return newTreeExpandedKeys;
    };

    useUpdateEffect(() => {
      if (treeSearchValue) {
        const targetList = state.treeTiledArray.filter((item) => {
          const labelValue = (item[fieldNames.label] || '') as string;
          if (customSearchRule) {
            return customSearchRule(item, treeSearchValue);
          }
          return String(labelValue)
            .toLowerCase()
            .includes(treeSearchValue.toLowerCase());
        });
        const treeExpandedKeysNew = getTreeExpandedKeys(
          targetList.map((item) => item[fieldNames.value]),
          true
        );
        setTreeExpandedKeys(Array.from(new Set(treeExpandedKeysNew)));
      } else {
        setTreeExpandedKeys([]);
      }
    }, [treeSearchValue]);
    useImperativeHandle(ref, () => {
      return {
        onClearSelectorList: () => {
          void actions.resetTreeList();
        },
        getTreeDataList: () => {
          return state.treeList;
        },
        getTreeTiledArray: () => state.treeTiledArray,
        getTreeTiledArrayMap: () => state.treeTiledArrayMap,
        onChangeExpandedKeys: (dataList) => {
          setTreeExpandedKeys(dataList);
        },
        onSelectAll: () => {
          const targetList = state.treeTiledArray.map(
            (temp) => temp[fieldNames.value]
          );
          setTreeExpandedKeys(targetList);
          onChange?.(targetList, undefined, state.treeList, undefined);
        },
        onCancelSelectAll: () => {
          setTreeExpandedKeys([]);
          onChange?.(undefined);
        },
        onExpandedAll: () => {
          const targetList = state.treeTiledArray.map(
            (temp) => temp[fieldNames.value]
          );
          setTreeExpandedKeys(targetList);
        },
        onCancelExpandedAll: () => {
          setTreeExpandedKeys([]);
        },
        onRefresh: () => {
          forceUpdate();
        },
      };
    });

    const onExpand = useMemoizedFn((expandedKeys) => {
      setTreeExpandedKeys(expandedKeys as string[]);
    });

    const onInnerChange = (
      selectedKey?: TreeWrapperValue,
      operateNodeData?: TPlainObject[] | TPlainObject,
      operateAllNodeDataList?: TPlainObject[],
      operateInfo?: TPlainObject
    ) => {
      innerValueListRef.current = toArray(selectedKey);
      if (toArray(selectedKey).length === 0) {
        const nodeValue = operateInfo?.node[fieldNames.value];
        const target = state.treeTiledArrayMap[nodeValue];
        props.onChange?.(undefined, target, [target], operateInfo);
      } else {
        props.onChange?.(
          selectedKey,
          operateNodeData,
          operateAllNodeDataList,
          operateInfo
        );
      }
    };

    const onTreeChangeHandle = useMemoizedFn((checkedData, operateInfo) => {
      // 最终checkValueList
      let finalCheckedValueList: Array<number | string> = [];
      // 完整checkValueList
      let completeCheckedValueList: Array<number | string> = [];

      if (checkable) {
        finalCheckedValueList = props.checkStrictly
          ? checkedData.checked
          : checkedData;
      } else {
        finalCheckedValueList = checkedData;
        if (required && !toArray(checkedData).length) {
          return;
        }
        if (disableNodeSelect) {
          const target = state.treeTiledArrayMap[checkedData[0]];
          const x = target ? disableNodeSelect(target) : false;
          if (x) {
            return;
          }
        }
      }
      finalCheckedValueList = toArray(finalCheckedValueList);

      if (!checkable) {
        completeCheckedValueList = finalCheckedValueList;
      } else {
        if (showSearch && treeSearchValue && !searchTypeHighlight) {
          // 数据源被过滤情况
          const operateNode = operateInfo?.node;
          if (props.checkStrictly) {
            if (operateInfo.checked == false) {
              finalCheckedValueList = valueList.filter(
                (item) => item != operateNode[fieldNames.value]
              );
            } else {
              finalCheckedValueList = Array.from(
                new Set([...valueList, ...finalCheckedValueList])
              );
            }
            completeCheckedValueList = finalCheckedValueList;
          } else {
            // 非checkStrictly=true情况
            const propsCompleteValues =
              checkableType === '2' || checkableType === '3'
                ? completionParentChildrenNode(
                    valueList,
                    state.treeTiledArrayMap,
                    fieldNames.children,
                    fieldNames.value
                  )
                : valueList;
            if (operateInfo.checked == false) {
              finalCheckedValueList = removeTargetNodeAndAllChildrenNode(
                [...propsCompleteValues],
                operateNode,
                fieldNames.children,
                fieldNames.value
              );
            } else {
              finalCheckedValueList = Array.from(
                new Set([...propsCompleteValues, ...finalCheckedValueList])
              );
            }
            completeCheckedValueList = getActualSelectKeyList(
              finalCheckedValueList,
              state.treeTiledArrayMap,
              fieldNames.children,
              fieldNames.value
            );
          }
        } else {
          // 无搜索情况
          completeCheckedValueList = finalCheckedValueList;
        }

        if (!props.checkStrictly) {
          if (checkableType === '2') {
            finalCheckedValueList = removeAllParentNode(
              completeCheckedValueList,
              state.treeTiledArrayMap,
              fieldNames.children
            );
          } else if (checkableType === '3') {
            finalCheckedValueList = removeAllSelectLeafNode(
              completeCheckedValueList,
              state.treeTiledArrayMap,
              fieldNames.children,
              fieldNames.value
            );
          } else if (checkableResponseParentNode == false) {
            finalCheckedValueList = removeAllParentNode(
              completeCheckedValueList,
              state.treeTiledArrayMap,
              fieldNames.children
            );
          } else {
            finalCheckedValueList = completeCheckedValueList;
          }
        }
      }

      const completeCheckedItemList = completeCheckedValueList.map((item) => {
        return state.treeTiledArrayMap[item];
      });
      if (labelInValue) {
        const lvValue = labelInValueFieldNamesMerge.value;
        const lvLabel = labelInValueFieldNamesMerge.label;
        const finalCheckedList = finalCheckedValueList.map((item) => {
          return {
            [lvLabel]: state.treeTiledArrayMap[item][fieldNames.label],
            [lvValue]: state.treeTiledArrayMap[item][fieldNames.value],
          };
        });
        if (checkable) {
          onInnerChange(
            finalCheckedList,
            finalCheckedValueList.map((item) => {
              return state.treeTiledArrayMap[item];
            }),
            completeCheckedItemList,
            operateInfo
          );
        } else {
          onInnerChange(
            finalCheckedList[0],
            state.treeTiledArrayMap[finalCheckedValueList[0]],
            completeCheckedItemList,
            operateInfo
          );
        }
      } else {
        if (checkable) {
          onInnerChange(
            finalCheckedValueList,
            finalCheckedValueList.map((item) => {
              return state.treeTiledArrayMap[item];
            }),
            completeCheckedItemList,
            operateInfo
          );
        } else {
          onInnerChange(
            finalCheckedValueList[0],
            state.treeTiledArrayMap[finalCheckedValueList[0]],
            completeCheckedItemList,
            operateInfo
          );
        }
      }
    });

    const treeItemDataAdapterHandle = useMemoizedFn((dataItem) => {
      return treeItemDataAdapter?.(dataItem);
    });

    const originalDataList = useMemo(() => {
      const list = cloneState(state.treeList || []);
      if (treeSearchValue && searchResultType !== 'highlight') {
        const resultList = treeFilter(
          cloneState(state.treeList || []),
          (node) => {
            if (customSearchRule) {
              return customSearchRule(node, treeSearchValue);
            }
            const value = node[fieldNames.label]?.toLowerCase();
            return value.indexOf(treeSearchValue.toLowerCase()) >= 0;
          },
          { childrenName: fieldNames.children }
        );
        return resultList;
      }
      return list;
    }, [
      customSearchRule,
      fieldNames.children,
      fieldNames.label,
      searchResultType,
      state.treeList,
      treeSearchValue,
    ]);

    const treeData = useMemo(() => {
      const loop = (data: TAny[]): TAny[] =>
        data?.map((item) => {
          const adapterItem = treeItemDataAdapterHandle?.(item) || item;
          const disabled = disabledCanUse ? undefined : adapterItem.disabled;
          const strTitle = (adapterItem[fieldNames.label] || '') as string;
          let titleDom;
          if (treeSearchValue && isString(strTitle)) {
            const strTitleNew = strTitle?.toLowerCase();
            const index = strTitleNew.indexOf(treeSearchValue.toLowerCase());
            const beforeStr = strTitle.substring(0, index);
            const afterStr = strTitle.slice(index + treeSearchValue.length);
            const centerStr = strTitle.slice(
              index,
              index + treeSearchValue.length
            );
            titleDom =
              index > -1 ? (
                <span
                  className={classNames({
                    'v-tree-item-disabled': adapterItem.disabled,
                  })}
                >
                  {beforeStr}
                  <span className="site-tree-search-value">{centerStr}</span>
                  {afterStr}
                </span>
              ) : null;
          }
          if (!titleDom && adapterItem.disabled) {
            titleDom = (
              <span
                className={classNames({
                  'v-tree-item-disabled': adapterItem.disabled,
                })}
              >
                {strTitle}
              </span>
            );
          }
          const children = adapterItem[fieldNames.children];
          return {
            ...adapterItem,
            disabled,
            [fieldNames.label]: titleDom || strTitle,
            _treeItemName: strTitle,
            _disabled: adapterItem.disabled,
            [fieldNames.children]:
              isArray(children) && children.length > 0
                ? loop(children)
                : undefined,
          };
        });
      return loop(originalDataList);
    }, [
      originalDataList,
      treeItemDataAdapterHandle,
      disabledCanUse,
      fieldNames.label,
      fieldNames.children,
      treeSearchValue,
    ]);
    const loadData = useMemoizedFn((dataItem: TPlainObject) => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise<void>(async (resolve, reject) => {
        if (dataItem[fieldNames.children]) {
          resolve();
          return;
        }
        try {
          const respData = await props.loadDataServiceConfig?.onRequest?.(
            props.loadDataServiceConfig?.getParams?.(dataItem) || {}
          );
          const respDataList = (
            props.loadDataServiceConfig?.onRequestResultAdapter
              ? props.loadDataServiceConfig.onRequestResultAdapter(
                  respData as unknown as TPlainObject
                )
              : respData
          ) as TPlainObject[];

          void actions
            .treeListAppendChildren({
              value: dataItem[fieldNames.value],
              appendList: respDataList,
              childrenName: fieldNames.children,
              valueName: fieldNames.value,
            })
            .then((allState) => {
              onChangeSelectorList(allState.treeList as TPlainObject[]);
              resolve();
            });
        } catch (error: TAny) {
          void message.error(error.message || '数据加载异常...');
          // 此处失败后，会重试“loadData”请求多次
          reject();
        }
      });
    });

    const isLeafNode = useMemoizedFn((nodeData) => {
      const children = nodeData?.[fieldNames.children];
      // 当loadDataFlag=true，考虑叶子节点显示问题
      const loadDataFlag = props.loadDataFlag;
      const loadHasChildren = loadDataFlag ? !nodeData.isLeaf : false;
      const hasChildren = (children && children.length > 0) || loadHasChildren;
      return !hasChildren;
    });

    const titleRender = useMemoizedFn((nodeData) => {
      const stringLabel = nodeData._treeItemName || nodeData[fieldNames.label];
      const buttonOperateConfig = menus?.({
        ...nodeData,
        [fieldNames.label]: stringLabel,
      });
      if (buttonOperateConfig?.operateList.length) {
        return (
          <Fragment>
            <span
              className="tree-item-title"
              data-value={nodeData[fieldNames.value]}
            >
              {labelRender?.(nodeData) || nodeData?.[fieldNames.label]}
            </span>
            <ButtonOperate
              gap={5}
              {...buttonOperateConfig}
              dropdownMenuProps={{
                placement: 'bottomRight',
                isFixed: true,
                ...buttonOperateConfig?.dropdownMenuProps,
              }}
              className={classNames(
                'tree-item-title-operate',
                buttonOperateConfig.className
              )}
            />
          </Fragment>
        );
      }
      return (
        <Fragment>
          <span
            className="tree-item-title"
            data-value={nodeData[fieldNames.value]}
          >
            {labelRender?.(nodeData) || nodeData?.[fieldNames.label]}
          </span>
        </Fragment>
      );
    });

    const { run: onSearchChange } = useDebounceFn(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setTreeSearchValue(value);
        props.onSearchValueChange?.(value);
      },
      { wait: 300, leading: false, trailing: true }
    );

    const iconHandle = useMemoizedFn((treeData) => {
      if (!props.icon) return null;
      if (isValidElement(props.icon)) return props.icon;
      const isLeafFlag = isLeafNode(treeData.data);
      return props.icon({
        ...treeData,
        isParent: !isLeafFlag,
        isLeaf: isLeafFlag,
      });
    });

    const treeFieldNames = useMemo(() => {
      return {
        title: fieldNames.label,
        key: fieldNames.value,
        children: fieldNames.children,
      };
    }, [fieldNames]);
    const showTree =
      treeData.length > 0 && state.requestStatus !== 'request-error';
    const commonProps = {
      showLine: { showLeafIcon: false },
      titleRender,
      blockNode: true,
      switcherIcon: <CaretDownFilled />,
      onDrop: (info) => {
        if (onDropPrev) {
          const boo = onDropPrev(info);
          if (!boo) return;
        }
        const { dataList, dragNodeData } = onTreeDrop(
          originalDataList,
          fieldNames,
          info
        );
        onDropNodeHandle?.(dragNodeData);
        onChangeSelectorList(dataList);
      },
      expandedKeys: treeExpandedKeys,
      ...otherProps,
      fieldNames: treeFieldNames,
      treeData,
      onExpand,
      loadData: props.loadDataFlag ? loadData : undefined,
      style: { width: '100%' },
      icon: iconHandle,
      onClick: (e) => {
        if (clickParentNodeToggleExpand) {
          let targetEle = e.target;
          let value = targetEle.dataset.value;
          if (isUndefinedOrNull(value)) {
            targetEle = findParentsElement(e.target, (node: Element) => {
              return (
                node.classList?.contains('tree-item-title') ||
                node.classList?.contains('ant-tree-treenode')
              );
            });
            value = targetEle.dataset.value;
          }
          if (!isUndefinedOrNull(value)) {
            let treeExpandedTarget = false;

            if (props.loadDataFlag) {
              treeExpandedTarget = treeExpandedKeys.includes(value);
            } else {
              const target = state.treeTiledArrayMap[value];
              if (!target?.[fieldNames.children]?.length) {
                return;
              }
              treeExpandedTarget = treeExpandedKeys.includes(value);
            }

            if (treeExpandedTarget) {
              const newList = arrayRemove(treeExpandedKeys, value);
              setTreeExpandedKeys([...newList]);
            } else {
              setTreeExpandedKeys([...treeExpandedKeys, value]);
            }
          }
        }
      },
    };

    const checkedProps = {
      onCheck: onTreeChangeHandle,
      checkable,
      checkedKeys: valueList,
    };

    const selectedProps = {
      onSelect: onTreeChangeHandle,
      multiple: false,
      selectedKeys: valueList,
    };
    const sceneProps = checkable ? checkedProps : selectedProps;
    const cName = classNames(
      'v-tree-wrapper',
      `v-tree-wrapper-menu-${menuTriggerTypeNew}`,
      className
    );
    const isSearchEmpty =
      state.treeList.length > 0 && !showTree && treeSearchValue;
    return (
      <div className={cName} style={style}>
        {!!showSearch && (
          <div className="v-tree-wrapper-search-area" style={searchStyle}>
            <InputSearchWrapper
              className="v-tree-wrapper-search"
              placeholder={
                isUndefinedOrNull(searchPlaceholder)
                  ? '搜索'
                  : searchPlaceholder
              }
              onChange={onSearchChange}
              value={treeSearchValue}
              allowClear
            />
            {!!props.searchExtraElement && (
              <span className="v-tree-wrapper-search-extra">
                {props.searchExtraElement}
              </span>
            )}
          </div>
        )}

        {showTree ? (
          <div className="v-tree-wrapper-tree-wrapper">
            <Spin spinning={state.requestStatus === 'request-progress'} />
            <Tree
              {...sceneProps}
              {...commonProps}
              className="v-tree-wrapper-tree"
            ></Tree>
          </div>
        ) : (
          <RequestStatus
            status={state.requestStatus}
            loading={loading}
            messageConfig={{
              'request-success': isSearchEmpty ? '搜索结果为空' : '暂无数据',
              'request-error': state.requestErrorMessage,
              ...requestMessageConfig,
            }}
            errorButton={
              <Button type="primary" onClick={startDataSourceRequest}>
                重新获取数据
              </Button>
            }
          />
        )}
      </div>
    );
  }
);
